/** Strip to digits only — loan amounts are whole rupees (no paise). */
export function sanitizeCurrencyTyping(raw: string): string {
  let result = '';
  for (const char of raw) {
    if (char >= '0' && char <= '9') {
      result += char;
    }
  }
  return result;
}

/** Count significant characters (digits + decimal) before a cursor index. */
function countCurrencyCharsBefore(value: string, cursor: number): number {
  let count = 0;
  const end = Math.min(cursor, value.length);
  for (let i = 0; i < end; i++) {
    const char = value[i];
    if ((char >= '0' && char <= '9') || char === '.') {
      count++;
    }
  }
  return count;
}

/** Map a currency-char index back to a display cursor position. */
function cursorFromCurrencyCharIndex(value: string, charIndex: number): number {
  if (charIndex <= 0) {
    return 0;
  }
  let count = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    if ((char >= '0' && char <= '9') || char === '.') {
      count++;
      if (count >= charIndex) {
        return i + 1;
      }
    }
  }
  return value.length;
}

/** Format while typing: Indian grouping on whole rupee amounts. */
export function formatInrAmountLive(sanitized: string): string {
  if (!sanitized) {
    return '';
  }

  const intNum = Number.parseInt(sanitized, 10);
  return Number.isFinite(intNum)
    ? intNum.toLocaleString('en-IN', { maximumFractionDigits: 0 })
    : '';
}

/** Apply formatted currency text and restore the caret after grouping changes. */
export function applyCurrencyInputDisplay(
  input: HTMLInputElement,
  display: string,
  rawBefore: string,
  cursorBefore: number,
): void {
  const charIndex = countCurrencyCharsBefore(rawBefore, cursorBefore);
  input.value = display;
  const nextCursor = cursorFromCurrencyCharIndex(display, charIndex);
  input.setSelectionRange(nextCursor, nextCursor);
}

/** Parse a display or raw currency string into a number. */
export function parseInrAmount(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value) : null;
  }
  const cleaned = sanitizeCurrencyTyping(String(value).replace(/,/g, ''));
  if (!cleaned || cleaned === '.') {
    return null;
  }
  const num = Number.parseInt(cleaned, 10);
  return Number.isFinite(num) ? num : null;
}

/** Round to whole rupees for INR currency values. */
export function roundInrAmount(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value);
}

/** Format a numeric amount using Indian grouping with no decimal places (e.g. 1,23,456). */
export function formatInrAmount(
  value: string | number | null | undefined,
): string {
  const num = parseInrAmount(value);
  if (num === null) {
    return '';
  }
  return roundInrAmount(num).toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  });
}
